import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
// import { validateEnvironment, logEnvironmentInfo } from './utils/environment';

// Use env var or derive protocol from page (HTTPS page requires HTTPS DSN to avoid Mixed Content)
const sentryBase = "a575c14ecb434b3bb2514fd891e525f1@sentry20.pervasive-tech.com:9000/134";
const defaultProtocol =
  typeof window !== "undefined" && window.location.protocol === "https:" ? "https" : "http";
const sentryDsn = process.env.REACT_APP_SENTRY_DSN || `${defaultProtocol}://${sentryBase}`;
const sentryEnv = process.env.NODE_ENV || "production";

const DATA_URL_PLACEHOLDER = "[base64 image redacted]";

function isBase64DataUrl(str: string): boolean {
  return (
    (str.startsWith("data:") && str.includes("base64,")) ||
    (str.length > 200 && str.includes("base64,"))
  );
}

function sanitizeDataUrls(obj: unknown): unknown {
  if (typeof obj === "string") {
    return isBase64DataUrl(obj) ? DATA_URL_PLACEHOLDER : obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeDataUrls);
  }
  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, sanitizeDataUrls(v)])
    );
  }
  return obj;
}

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv,
    release: process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_VERSION || undefined,
    integrations: [
      new Sentry.BrowserTracing(),
    ],
    tracesSampleRate: sentryEnv === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    maxBreadcrumbs: 20,
    maxValueLength: 1000,
    normalizeDepth: 5,
    beforeBreadcrumb(breadcrumb) {
      // Strip base64 data URLs from breadcrumbs to avoid 413 Payload Too Large
      if (breadcrumb.message && typeof breadcrumb.message === "string") {
        breadcrumb.message = sanitizeDataUrls(breadcrumb.message) as string;
      }
      if (breadcrumb.data && typeof breadcrumb.data === "object") {
        breadcrumb.data = sanitizeDataUrls(breadcrumb.data) as Record<string, unknown>;
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // Truncate oversized payloads to avoid 413 Payload Too Large
      const eventStr = JSON.stringify(event);
      if (eventStr.length > 100_000) {
        return {
          ...event,
          message: (event.message || "Error").slice(0, 500) + " [truncated - payload too large]",
          contexts: {},
          extra: { _truncated: true, _originalSize: eventStr.length },
          breadcrumbs: (event.breadcrumbs || []).slice(-5),
        };
      }
      return event;
    },
  });
}

// Validate environment variables early
// try {
//   validateEnvironment();
//   logEnvironmentInfo();
// } catch (error) {
//   console.error('Environment validation failed:', error);
  
//   // Show error message in the DOM
//   const rootElement = document.getElementById('root');
//   if (rootElement) {
//     rootElement.innerHTML = `
//       <div style="
//         display: flex;
//         flex-direction: column;
//         align-items: center;
//         justify-content: center;
//         min-height: 100vh;
//         padding: 20px;
//         font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
//         background-color: #f5f5f5;
//       ">
//         <div style="
//           background: white;
//           padding: 40px;
//           border-radius: 8px;
//           box-shadow: 0 4px 16px rgba(0,0,0,0.1);
//           text-align: center;
//           max-width: 500px;
//         ">
//           <h1 style="color: #e53935; margin-bottom: 16px;">Error de Configuración</h1>
//           <p style="color: #666; margin-bottom: 24px;">
//             La aplicación no puede iniciarse debido a un problema de configuración.
//           </p>
//           <p style="color: #999; font-size: 14px;">
//             ${error instanceof Error ? error.message : 'Error desconocido'}
//           </p>
//         </div>
//       </div>
//     `;
//   }
  
//   // Don't render the React app if environment validation fails
//   throw error;
// }

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    {sentryDsn ? (
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>} showDialog>
        <App />
      </Sentry.ErrorBoundary>
    ) : (
      <App />
    )}
  </React.StrictMode>
);

reportWebVitals();
